import { useNavigate, useParams, useLocation } from 'react-router-dom';
import Alert from '@/components/alert/alert-component';
import FormWrapper from '@/components/form-wrapper/form-wrapper-component';
import { useMutation } from '@/hooks/use-api';
import createCompanyTemplateApi from '@/api/post/company/company-template/create-company-template';
import uploadToTempS3Base64 from '@/utils/upload-to-s3-base64';
import uploadToTempS3File from '@/utils/upload-to-s3-file';
import { useState } from 'react';
import { removeUndefinedProperties } from '@/utils/utils';
import getCompanyTemplateApi from '@/api/get/company/company-template/get-company-template';
import TemplateForm from './template-form';
import { TemplateContextProvider } from './context-provider';
import createFlatOptions, { recursiveDuplicateTemplateOptions } from './utils';

/**
 * Add template component.
 * @returns {JSX.Element} The rendered Template Add page component.
 */
const TemplateNew = () => {
  const [loading, setLoading] = useState(false);
  const [prices, setPrices] = useState([]);
  const [options, setOptions] = useState([]);
  const { state: locationState } = useLocation();
  const navigate = useNavigate();
  const { id } = useParams();

  const contextValue = {
    prices,
    options,
    setOptions,
    setPrices,
    uniqueContextId: Symbol('product-options-context'),
  };

  const [createTemplateApi, { loading: createTemplateLoading }] = useMutation(
    createCompanyTemplateApi,
  );

  return (
    <TemplateContextProvider value={contextValue}>
      <FormWrapper
        defaultValues={async () => {
          if (locationState) {
            const template = await getCompanyTemplateApi(
              id,
              locationState.isTemplateDuplicate,
            );
            delete template.data.template.id;
            delete template.data.template.name;
            template.data.templateOptions = recursiveDuplicateTemplateOptions(
              template.data.templateOptions,
            );

            template.data.categories =
              template.data?.categories?.map((c) => c.category_id) ?? [];

            return template.data;
          }
          return { prices: [{ id: '', description: '' }] };
        }}
        onSubmit={async (values) => {
          try {
            // create template
            setLoading(true);
            const {
              template,
              categories,
              templateLinks,
              templateOptions,
              templateGallery,
              templateDocuments,
            } = values;

            delete template.id;
            delete template.created_at;
            delete template.updated_at;

            if (template?.thumbnail?.file) {
              const { file, fileName, fileType } = values.template.thumbnail;
              await uploadToTempS3Base64(
                file,
                `image-upload/${fileName}`,
                fileType,
              ).then((data) => {
                template.thumbnail = `${data.id}`;
              });
            }

            const newGallery = templateGallery.map(async (gallery, index) => {
              if (locationState?.isTemplateDuplicate && gallery.id) {
                return {
                  id: 'duplicate',
                  image: gallery.image,
                  order: index,
                };
              }
              const { file } = gallery;
              if (file) {
                return uploadToTempS3File(
                  file,
                  `image-upload/${file.name}-img-${index}`,
                ).then((data) => {
                  const fileKey = data?.id;
                  const updatedGallery = {
                    image: fileKey,
                    order: index,
                  };
                  return updatedGallery;
                });
              }
            });
            const addGallery = await Promise.all(newGallery);

            const newDocuments = templateDocuments.map(async (doc) => {
              if (locationState?.isTemplateDuplicate && doc.id) {
                return {
                  id: 'duplicate',
                  document: doc.document,
                  name: doc.name,
                };
              }
              const { file, fileName } = doc.file;
              if (file) {
                return uploadToTempS3File(
                  file,
                  `document-upload/${fileName}`,
                ).then((data) => {
                  const newDoc = {
                    document: data.id,
                    name: doc.name,
                  };

                  return newDoc;
                });
              }
            });
            const addDocuments = await Promise.all(newDocuments);

            const newOptions =
              templateOptions.length > 0
                ? createFlatOptions(templateOptions, [])
                : undefined;

            let addCategories = categories;

            if (locationState?.isTemplateDuplicate) {
              const tempCategories = categories.map((a) => {
                if (a.category_id) {
                  return a.category_id;
                }
                return a;
              });
              // Ensure that there are no duplicates being sent through to the server.
              addCategories = [...new Set([...tempCategories])];
            }

            let newTemplateLinks = templateLinks;
            if (locationState?.isTemplateDuplicate) {
              newTemplateLinks = templateLinks?.map((link) => ({
                link: link?.link,
              }));
            }

            const payload = {
              template,
              categories: { add: addCategories },
              templateGallery: { add: addGallery },
              templateDocuments: { add: addDocuments },
              templateLinks: { add: newTemplateLinks },
              templateOptions: newOptions,
            };

            createTemplateApi(id, removeUndefinedProperties(payload))
              .then(() => {
                navigate(-1);
                Alert({
                  icon: 'success',
                  text: 'Template created successfully',
                });
                setLoading(false);
              })
              .catch(() => {
                Alert({
                  icon: 'error',
                  text: 'Template failed to create',
                });
                setLoading(false);
              });
          } catch (e) {
            setLoading(false);
          }
        }}
      >
        <TemplateForm
          type="edit"
          isLoading={createTemplateLoading || loading}
        />
      </FormWrapper>
    </TemplateContextProvider>
  );
};

export default TemplateNew;
